import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { X } from "lucide-react";
import { Button } from "@/components/ui/button";

interface ModalProps {
  isOpen: boolean;
  onClose: () => void;
  title: React.ReactNode;
  children: React.ReactNode;
  footer?: React.ReactNode;
  size?: 'sm' | 'md' | 'lg' | 'xl' | 'full' | 'fit';
  showHeader?: boolean;
  variant?: 'default' | 'dark';
}

// Rimette il contenitore dei portali in `pointer-events: none` (la classe CSS)
// non appena nessuna modale è più renderizzata al suo interno. Rilegge sempre
// il nodo dal DOM: quello catturato in una closure può essere un contenitore
// già rimosso e sostituito da un altro.
const releasePortalPointerEvents = () => {
  const container = document.getElementById('modal-portal');
  if (!container) return;
  if (container.querySelectorAll('[data-modal-portal]').length === 0) {
    container.style.pointerEvents = '';
  }
};

// Blocco dello scorrimento condiviso fra tutte le modali aperte.
//
// Prima ogni istanza scriveva da sola `body.style.overflow`: con due modali
// annidate (dettaglio attivita' + carosello foto) la chiusura di quella interna
// sbloccava lo scorrimento anche se l'altra era ancora aperta. Da qui il contatore.
//
// Su iOS `overflow: hidden` sul body non ferma lo scorrimento quando la pagina e'
// gia' scrollata: serve fissare il body e compensare con `top` negativo,
// ripristinando la posizione alla riapertura.
let scrollLockCount = 0;
let lockedScrollY = 0;

const lockBodyScroll = () => {
  if (scrollLockCount++ > 0) return;
  lockedScrollY = window.scrollY;
  document.body.style.position = 'fixed';
  document.body.style.top = `-${lockedScrollY}px`;
  document.body.style.left = '0';
  document.body.style.right = '0';
  document.body.style.overflow = 'hidden';
};

const unlockBodyScroll = () => {
  if (scrollLockCount === 0) return;
  if (--scrollLockCount > 0) return;
  document.body.style.position = '';
  document.body.style.top = '';
  document.body.style.left = '';
  document.body.style.right = '';
  document.body.style.overflow = '';
  window.scrollTo(0, lockedScrollY);
};

export const Modal: React.FC<ModalProps> = ({
  isOpen,
  onClose,
  title,
  children,
  footer,
  size = 'md',
  showHeader = true,
  variant = 'default'
}) => {
  const [portalContainer, setPortalContainer] = useState<HTMLElement | null>(null);
  const [modalZIndex, setModalZIndex] = useState(60);
  // shouldRender keeps the DOM alive during the exit animation
  const [shouldRender, setShouldRender] = useState(isOpen);
  // isVisible drives the CSS transition classes
  const [isVisible, setIsVisible] = useState(false);
  const exitTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (isOpen) {
      if (exitTimerRef.current) clearTimeout(exitTimerRef.current);
      setShouldRender(true);
      // Defer two frames: first ensures initial "closed" classes are painted,
      // second triggers the transition to the "open" state
      requestAnimationFrame(() => requestAnimationFrame(() => setIsVisible(true)));
    } else {
      setIsVisible(false);
      // Keep DOM mounted for 200 ms to let the exit animation finish
      exitTimerRef.current = setTimeout(() => setShouldRender(false), 200);
    }
    return () => {
      if (exitTimerRef.current) clearTimeout(exitTimerRef.current);
    };
  }, [isOpen]);

  useEffect(() => {
    // Il contenitore è un `fixed inset-0` esteso a tutto lo schermo: va creato solo
    // mentre una modale è davvero renderizzata. Crearlo al mount — anche a modale
    // chiusa, come faceva prima — lasciava un pannello invisibile sopra l'intera
    // pagina in ogni schermata che monta una Modal (la proposta condivisa ne monta
    // una per ogni card della timeline), e su iOS Safari `pointer-events: none` non
    // basta a farlo ignorare dal tocco.
    if (!shouldRender) {
      setPortalContainer(null);
      return;
    }

    let container = document.getElementById('modal-portal');
    if (!container) {
      container = document.createElement('div');
      container.id = 'modal-portal';
      container.className = 'fixed inset-0 z-[60] pointer-events-none';
      document.body.appendChild(container);
    }
    setPortalContainer(container);

    // Calculate dynamic z-index based on existing modals
    setModalZIndex(60 + document.querySelectorAll('[data-modal-portal]').length);

    return () => {
      setTimeout(() => {
        // Non usare il `container` della closure: può essere un nodo già
        // staccato dal DOM, e in quel caso il contenitore vivo non verrebbe
        // mai ripulito.
        const current = document.getElementById('modal-portal');
        const existingModals = document.querySelectorAll('[data-modal-portal]');
        if (existingModals.length === 0 && current?.parentNode) {
          current.parentNode.removeChild(current);
        }
      }, 0);
    };
  }, [shouldRender]);

  // Handle escape key
  useEffect(() => {
    if (!isOpen) return;
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen, onClose]);

  // Prevent body scroll when modal is open
  useEffect(() => {
    if (!isOpen) return;
    lockBodyScroll();
    return unlockBodyScroll;
  }, [isOpen]);

  // iOS Safari fix: pointer-events:none on a fixed parent blocks touch events on
  // children even when they have pointer-events:auto (known WebKit bug).
  // Toggle the portal container to pointer-events:auto while any modal is rendered.
  useEffect(() => {
    if (!shouldRender) {
      // React ha già smontato i nodi di questa modale: se non ne resta
      // nessun'altra dentro al contenitore, torna a pointer-events:none.
      releasePortalPointerEvents();
      return;
    }

    const container = document.getElementById('modal-portal');
    if (container) container.style.pointerEvents = 'auto';

    // Il rilascio deve passare anche dal cleanup. Se il componente viene
    // smontato mentre la modale è ancora aperta (cambio rotta, o un parent
    // che smette di renderizzarla insieme al flag di apertura) l'effetto non
    // viene mai rieseguito con shouldRender=false: il contenitore resterebbe
    // `pointer-events: auto`, cioè un pannello invisibile a tutto schermo che
    // intercetta i click di tutta la pagina. Il setTimeout aspetta che React
    // abbia rimosso i nodi prima di contare le modali rimaste.
    return () => { setTimeout(releasePortalPointerEvents, 0); };
  }, [shouldRender]);

  if (!shouldRender || !portalContainer) return null;

  const sizeClasses = {
    sm: 'max-w-md',
    md: 'max-w-2xl',
    lg: 'max-w-4xl',
    xl: 'max-w-7xl',
    full: 'max-w-[95vw]',
    fit: 'w-fit max-w-[95vw]'
  };

  const isDark = variant === 'dark';

  const modalContent = (
    <div className="fixed inset-0 flex items-center justify-center pointer-events-auto" data-modal-portal style={{ zIndex: modalZIndex }}>
      {/* Backdrop — fades in/out */}
      <div
        className={`absolute inset-0 bg-black/50 backdrop-blur-sm transition-opacity duration-200 ${isVisible ? 'opacity-100' : 'opacity-0'}`}
        onClick={onClose}
      />

      {/* Panel — fades + scales in/out */}
      <div className={`relative ${size === 'fit' ? '' : 'w-full'} ${sizeClasses[size]} mx-4 ${size === 'full' ? 'h-[95vh] flex flex-col' : 'max-h-[95vh]'} overflow-hidden rounded-lg ${isDark ? 'bg-black/90' : 'bg-white'} shadow-xl transition-all duration-200 ease-out ${isVisible ? 'opacity-100 scale-100' : 'opacity-0 scale-95'}`}>
        {/* Header */}
        {showHeader && (
          <div className="flex items-center justify-between border-b border-gray-200 px-6 py-4">
            <h4 className="text-gray-900">{title}</h4>
            <Button
              variant="ghost"
              size="sm"
              onClick={onClose}
              className="h-8 w-8 p-0"
            >
              <X className="h-4 w-4" />
            </Button>
          </div>
        )}

        {/* Content */}
        <div className={`overflow-y-auto ${size === 'full' ? 'flex-1 min-h-0' : (
          showHeader
            ? (footer ? 'max-h-[calc(95vh-180px)]' : 'max-h-[calc(95vh-120px)]')
            : (footer ? 'max-h-[calc(95vh-120px)]' : 'max-h-[calc(95vh-60px)]')
        )}`}>
          {children}
        </div>

        {/* Footer */}
        {footer && (
          <div className="flex items-center justify-end gap-3 border-t border-gray-200 px-6 py-4">
            {footer}
          </div>
        )}
      </div>
    </div>
  );

  return createPortal(modalContent, portalContainer);
};
